import { useState } from 'react'
import { lumi } from '../lib/lumi'

export interface CommenterUser {
  userId: string
  username: string
  email: string
  nickname: string
  profileImageUrl?: string
  accountType: string
  subscriptionStatus?: string
  stripeCustomerId?: string
}

interface RegisterData {
  username: string
  password: string
  email: string
  nickname?: string
}

interface LoginData {
  username: string
  password: string
}

export function useCommenterAuth() {
  const [user, setUser] = useState<CommenterUser | null>(() => {
    // Check localStorage for existing session
    const stored = localStorage.getItem('commenterUser')
    return stored ? JSON.parse(stored) : null
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const register = async (data: RegisterData) => {
    setLoading(true)
    setError(null)
    try {
      const result = await lumi.functions.invoke('userAuth', {
        method: 'POST',
        body: {
          action: 'register',
          ...data
        }
      })

      if (result.success && result.user) {
        // Store user in localStorage
        localStorage.setItem('commenterUser', JSON.stringify(result.user))
        localStorage.setItem('commenterToken', result.token)
        setUser(result.user)
        return result.user
      } else {
        throw new Error(result.error || 'Registration failed')
      }
    } catch (err: any) {
      const errorMessage = err?.data?.error || err.message || 'Registration failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const login = async (data: LoginData) => {
    setLoading(true)
    setError(null)
    try {
      const result = await lumi.functions.invoke('userAuth', {
        method: 'POST',
        body: {
          action: 'login',
          ...data
        }
      })

      if (result.success && result.user) {
        // Fetch full profile with subscription status
        const { list: profiles } = await lumi.entities.userProfiles.list({
          filter: { userId: result.user.userId }
        })

        const profile = profiles[0]
        const fullUser = {
          ...result.user,
          subscriptionStatus: profile?.subscriptionStatus || 'inactive',
          stripeCustomerId: profile?.stripeCustomerId || ''
        }

        // Store user in localStorage
        localStorage.setItem('commenterUser', JSON.stringify(fullUser))
        localStorage.setItem('commenterToken', result.token)
        setUser(fullUser)
        return fullUser
      } else {
        throw new Error(result.error || 'Login failed')
      }
    } catch (err: any) {
      const errorMessage = err?.data?.error || err.message || 'Login failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    localStorage.removeItem('commenterUser')
    localStorage.removeItem('commenterToken')
    setUser(null)
    setError(null)
  }

  const refreshUser = async () => {
    if (!user) return null

    try {
      const { list: profiles } = await lumi.entities.userProfiles.list({
        filter: { userId: user.userId }
      })

      if (profiles.length > 0) {
        const profile = profiles[0]
        const updatedUser = {
          ...user,
          nickname: profile.nickname || user.username,
          profileImageUrl: profile.profileImageUrl,
          subscriptionStatus: profile.subscriptionStatus,
          stripeCustomerId: profile.stripeCustomerId
        }
        localStorage.setItem('commenterUser', JSON.stringify(updatedUser))
        setUser(updatedUser)
        return updatedUser
      }
    } catch (err) {
      console.error('Failed to refresh user:', err)
    }
    return user
  }

  return {
    user,
    loading,
    error,
    register,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    hasActiveSubscription: user?.subscriptionStatus === 'active'
  }
}
